import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { toastError, toastSuccess } from "../../../helpers/Toast";

const periodLabels = {
  DAILY: "Dzienna",
  MONTHLY: "Miesięczna",
  ANNUAL: "Roczna"
};

const salesBasisLabels = {
  PLANNED_SALES: "sprzedaż planowana",
  DAILY_FORECAST: "prognoza dzienna",
  MONTHLY_FORECAST_DAILY_SHARE: "1 dzień prognozy miesięcznej",
  ANNUAL_FORECAST_DAILY_SHARE: "1 dzień prognozy rocznej",
  NONE: "brak"
};

const numberValue = value => value === null || value === undefined || value === "" ? null : Number(value);
const money = value => value === null || value === undefined ? "—" : `${Number(value).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
const metric = value => value === null || value === undefined ? "—" : Number(value).toLocaleString("pl-PL", { maximumFractionDigits: 2 });
const hours = value => value === null || value === undefined ? "—" : `${Number(value).toLocaleString("pl-PL", { maximumFractionDigits: 2 })} h`;
const percent = value => value === null || value === undefined ? "—" : `${Number(value).toLocaleString("pl-PL", { maximumFractionDigits: 2 })}%`;
const signed = (value, suffix = "") => {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toLocaleString("pl-PL", { maximumFractionDigits: 2 })}${suffix}`;
};

const parseDate = value => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

const dateString = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const timeString = date => `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

const resolveForecastRange = (periodType, referenceDate) => {
  const date = parseDate(referenceDate) || new Date();
  if (periodType === "MONTHLY") {
    return {
      dateFrom: dateString(new Date(date.getFullYear(), date.getMonth(), 1, 12, 0, 0, 0)),
      dateTo: dateString(new Date(date.getFullYear(), date.getMonth() + 1, 0, 12, 0, 0, 0))
    };
  }
  if (periodType === "ANNUAL") {
    return {
      dateFrom: `${date.getFullYear()}-01-01`,
      dateTo: `${date.getFullYear()}-12-31`
    };
  }
  const day = dateString(date);
  return { dateFrom: day, dateTo: day };
};

const emptyDayForm = date => ({
  planningDate: date,
  plannedSales: "",
  actualSales: "",
  expectedGuests: "",
  weatherForecast: "",
  weatherProvider: null,
  weatherCode: null,
  weatherTemperatureMin: null,
  weatherTemperatureMax: null,
  weatherPrecipitationSum: null,
  weatherPrecipitationProbabilityMax: null,
  weatherWindSpeedMax: null,
  weatherFetchedAt: null,
  weatherDemandAdjustmentPercent: "",
  targetColPercent: "",
  targetSpmh: "",
  laborHoursBudget: "",
  minimumStaff: "",
  maximumStaff: "",
  notes: "",
  version: null
});

const emptyForecastForm = referenceDate => ({
  id: null,
  periodType: "DAILY",
  referenceDate,
  salesForecast: "",
  guestsForecast: "",
  weatherSummary: "",
  notes: "",
  version: null
});

const deltaClass = value => Number(value || 0) > 0
  ? "workforce-planning-delta-positive"
  : Number(value || 0) < 0
    ? "workforce-planning-delta-negative"
    : "";

const WorkforcePlanningPanel = ({
  schedule,
  visibleRange,
  assignments,
  onEditAssignment,
  onDirectAssignmentChanged,
  departmentPlanningDefaults
}) => {
  const axiosPrivate = useAxiosPrivate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [dayForm, setDayForm] = useState(null);
  const [forecastForm, setForecastForm] = useState(null);
  const [sendHomeDay, setSendHomeDay] = useState(null);
  const [sendHomeTime, setSendHomeTime] = useState("14:00");
  const [sendHomeCandidates, setSendHomeCandidates] = useState([]);
  const [sendHomeLoading, setSendHomeLoading] = useState(false);
  const [sendHomeOverride, setSendHomeOverride] = useState(false);
  const [sendHomeReason, setSendHomeReason] = useState("");

  const assignmentSignature = useMemo(
    () => (assignments ?? []).map(item => `${item.id}:${item.version ?? ""}:${item.status}:${item.employeeId ?? ""}:${item.startsAt}:${item.endsAt}:${item.totalHourlyCost ?? ""}:${item.costComplete ?? ""}`).join("|"),
    [assignments]
  );

  const getErrorMessage = error => {
    const data = error?.response?.data;
    if (typeof data === "string") return data;
    return data?.message || error?.message || "Nie udało się wykonać operacji.";
  };

  const load = async () => {
    if (!schedule?.id || !visibleRange?.dateFrom || !visibleRange?.dateTo) return;
    setLoading(true);
    try {
      const response = await axiosPrivate.get(`/workforce-planning/schedules/${schedule.id}`, {
        params: { dateFrom: visibleRange.dateFrom, dateTo: visibleRange.dateTo }
      });
      setAnalysis(response.data);
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [schedule?.id, visibleRange?.dateFrom, visibleRange?.dateTo, assignmentSignature]);

  const openDay = day => {
    const plan = day.plan;
    setDayForm(plan ? {
      ...plan,
      planningDate: day.date,
      plannedSales: plan.plannedSales ?? "",
      actualSales: plan.actualSales ?? "",
      expectedGuests: plan.expectedGuests ?? "",
      weatherForecast: plan.weatherForecast ?? "",
      weatherDemandAdjustmentPercent: plan.weatherDemandAdjustmentPercent ?? "",
      targetColPercent: plan.targetColPercent ?? "",
      targetSpmh: plan.targetSpmh ?? "",
      laborHoursBudget: plan.laborHoursBudget ?? "",
      minimumStaff: plan.minimumStaff ?? "",
      maximumStaff: plan.maximumStaff ?? "",
      notes: plan.notes ?? ""
    } : emptyDayForm(day.date));
  };

  const saveDay = async event => {
    event.preventDefault();
    if (!dayForm) return;
    setSaving(true);
    try {
      await axiosPrivate.put(`/workforce-planning/departments/${schedule.departmentId}/days/${dayForm.planningDate}`, {
        departmentId: schedule.departmentId,
        planningDate: dayForm.planningDate,
        plannedSales: numberValue(dayForm.plannedSales),
        actualSales: numberValue(dayForm.actualSales),
        expectedGuests: numberValue(dayForm.expectedGuests),
        weatherForecast: dayForm.weatherForecast || null,
        weatherDemandAdjustmentPercent: numberValue(dayForm.weatherDemandAdjustmentPercent),
        targetColPercent: numberValue(dayForm.targetColPercent),
        targetSpmh: numberValue(dayForm.targetSpmh),
        laborHoursBudget: numberValue(dayForm.laborHoursBudget),
        minimumStaff: numberValue(dayForm.minimumStaff),
        maximumStaff: numberValue(dayForm.maximumStaff),
        notes: dayForm.notes || null,
        version: dayForm.version
      });
      setDayForm(null);
      await load();
      toastSuccess("Zapisano plan sprzedaży i budżetu pracy dla dnia.");
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const deleteDay = async () => {
    if (!dayForm?.id) return;
    setSaving(true);
    try {
      await axiosPrivate.delete(`/workforce-planning/departments/${schedule.departmentId}/days/${dayForm.planningDate}`);
      setDayForm(null);
      await load();
      toastSuccess("Usunięto plan ekonomiczny dnia.");
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const importWeather = async () => {
    if (!schedule?.departmentId || !visibleRange?.dateFrom || !visibleRange?.dateTo) return;
    setWeatherLoading(true);
    try {
      const response = await axiosPrivate.post(`/workforce-planning/departments/${schedule.departmentId}/weather/import`, null, {
        params: { dateFrom: visibleRange.dateFrom, dateTo: visibleRange.dateTo }
      });
      await load();
      toastSuccess(`Pobrano pogodę dla ${Array.isArray(response.data) ? response.data.length : 0} dni.`);
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setWeatherLoading(false);
    }
  };

  const openNewForecast = () => setForecastForm(emptyForecastForm(visibleRange?.dateFrom || schedule.dateFrom));

  const openForecast = item => setForecastForm({
    ...item,
    referenceDate: item.dateFrom,
    salesForecast: item.salesForecast ?? "",
    guestsForecast: item.guestsForecast ?? "",
    weatherSummary: item.weatherSummary ?? "",
    notes: item.notes ?? ""
  });

  const saveForecast = async event => {
    event.preventDefault();
    if (!forecastForm) return;
    const range = resolveForecastRange(forecastForm.periodType, forecastForm.referenceDate);
    const payload = {
      id: forecastForm.id,
      departmentId: schedule.departmentId,
      periodType: forecastForm.periodType,
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      salesForecast: numberValue(forecastForm.salesForecast),
      guestsForecast: numberValue(forecastForm.guestsForecast),
      weatherSummary: forecastForm.weatherSummary || null,
      notes: forecastForm.notes || null,
      version: forecastForm.version
    };
    setSaving(true);
    try {
      if (forecastForm.id) await axiosPrivate.put(`/workforce-planning/forecasts/${forecastForm.id}`, payload);
      else await axiosPrivate.post("/workforce-planning/forecasts", payload);
      setForecastForm(null);
      await load();
      toastSuccess("Zapisano prognozę.");
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const deleteForecast = async () => {
    if (!forecastForm?.id) return;
    setSaving(true);
    try {
      await axiosPrivate.delete(`/workforce-planning/forecasts/${forecastForm.id}`);
      setForecastForm(null);
      await load();
      toastSuccess("Usunięto prognozę.");
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const defaultSendHomeTime = date => {
    const now = new Date();
    if (date === dateString(now)) return timeString(now);
    const candidates = (assignments ?? [])
      .filter(item => item.status !== "CANCELLED" && item.status !== "OPEN" && item.employeeId != null)
      .filter(item => String(item.startsAt).slice(0, 10) === date)
      .map(item => String(item.startsAt).slice(11, 16))
      .sort();
    return candidates.length > 0 ? candidates[Math.floor(candidates.length / 2)] : "14:00";
  };

  const openSendHome = day => {
    setSendHomeDay(day);
    setSendHomeTime(defaultSendHomeTime(day.date));
    setSendHomeCandidates([]);
    setSendHomeOverride(false);
    setSendHomeReason("");
  };

  const loadSendHomeCandidates = async (day = sendHomeDay, time = sendHomeTime) => {
    if (!day?.date || !time) return;
    setSendHomeLoading(true);
    try {
      const proposedEndsAt = `${day.date}T${time}:00`;
      const response = await axiosPrivate.get(`/workforce-planning/schedules/${schedule.id}/days/${day.date}/send-home-recommendations`, {
        params: { proposedEndsAt }
      });
      setSendHomeCandidates(response.data ?? []);
    } catch (error) {
      setSendHomeCandidates([]);
      toastError(getErrorMessage(error));
    } finally {
      setSendHomeLoading(false);
    }
  };

  useEffect(() => {
    if (!sendHomeDay) return;
    const handle = window.setTimeout(() => loadSendHomeCandidates(sendHomeDay, sendHomeTime), 180);
    return () => window.clearTimeout(handle);
  }, [sendHomeDay?.date, sendHomeTime]);

  const applySendHome = async candidate => {
    const unsafe = candidate.dayUnderstaffedBeforeRelease || !candidate.minimumStaffRespected || !candidate.positionCoveragePreserved || !candidate.locationCoveragePreserved;
    if (unsafe && (!sendHomeOverride || !sendHomeReason.trim())) {
      toastError("Ta decyzja narusza zabezpieczenie obsady. Zaznacz świadome nadpisanie i podaj powód.");
      return;
    }
    setSaving(true);
    try {
      await axiosPrivate.post(`/workforce-planning/schedules/${schedule.id}/send-home`, {
        assignmentId: candidate.assignmentId,
        proposedEndsAt: `${sendHomeDay.date}T${sendHomeTime}:00`,
        assignmentVersion: candidate.assignmentVersion,
        overrideSafety: unsafe && sendHomeOverride,
        overrideReason: unsafe ? sendHomeReason.trim() : null
      });
      toastSuccess(`Skrócono zmianę pracownika ${candidate.employeeFullName} do ${sendHomeTime}.`);
      setSendHomeDay(null);
      setSendHomeCandidates([]);
      if (onDirectAssignmentChanged) await onDirectAssignmentChanged();
      await load();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (!analysis && loading) {
    return <div className="schedule-section workforce-planning-section"><div className="schedule-empty">Ładowanie danych sprzedaży i budżetu pracy…</div></div>;
  }

  const days = analysis?.days ?? [];
  const forecasts = analysis?.forecasts ?? [];

  return (
    <div className="schedule-section workforce-planning-section">
      <div className="schedule-section-heading">
        <div>
          <h6>Sprzedaż, prognozy i budżet pracy</h6>
          <div className="schedule-section-subtitle">
            Plan porównuje koszt i godziny grafiku z prognozą sprzedaży. Wykonanie wykorzystuje sprzedaż wykonaną oraz rzeczywiste godziny RCP. Jeżeli nie ma SPRZEDAŻ PLAN ani prognozy dziennej, miesięczna lub roczna prognoza jest rozkładana równomiernie na dni okresu.
          </div>
        </div>
        <div className="schedule-section-actions">
          <Button size="sm" variant="outline-secondary" onClick={load} disabled={loading || saving || weatherLoading}>Odśwież</Button>
          <Button size="sm" variant="outline-secondary" onClick={importWeather} disabled={saving || weatherLoading}>{weatherLoading ? "Pobieranie pogody…" : "Pobierz pogodę"}</Button>
          <Button size="sm" variant="outline-primary" onClick={openNewForecast} disabled={saving || weatherLoading}>Dodaj prognozę</Button>
        </div>
      </div>

      <div className={`workforce-planning-summary ${analysis?.economicAlertDays > 0 || analysis?.shortageDays > 0 ? "workforce-planning-summary-alert" : ""}`}>
        <span><b>Godziny PLAN / WYK:</b> {hours(analysis?.plannedHours)} / {hours(analysis?.actualHours)} <em className={deltaClass(analysis?.hoursDelta)}>({signed(analysis?.hoursDelta, " h")})</em></span>
        <span><b>Koszt PLAN / WYK:</b> {money(analysis?.plannedLaborCost)} / {money(analysis?.actualLaborCost)} <em className={deltaClass(analysis?.laborCostDelta)}>({signed(analysis?.laborCostDelta, " zł")})</em></span>
        <span><b>Sprzedaż PLAN / WYK:</b> {money(analysis?.plannedSales)} / {money(analysis?.actualSales)}</span>
        <span><b>%COL PLAN / WYK:</b> {percent(analysis?.colPlanPercent)} / {percent(analysis?.colActualPercent)}</span>
        <span><b>SPMH PLAN / WYK:</b> {analysis?.spmhPlan == null ? "—" : `${metric(analysis.spmhPlan)} zł/h`} / {analysis?.spmhActual == null ? "—" : `${metric(analysis.spmhActual)} zł/h`}</span>
        <span><b>Budżet godzin:</b> {analysis?.laborHoursBudgetDays > 0 ? `${hours(analysis?.laborHoursBudget)} · ${analysis.laborHoursBudgetDays} dni` : "—"}</span>
        <span><b>Dni ponad budżet:</b> {analysis?.laborHoursBudgetExceededDays ?? 0}</span>
        <span><b>Dni z brakami:</b> {analysis?.shortageDays ?? 0}</span>
        <span><b>Dni z nadmiarem:</b> {analysis?.overstaffedDays ?? 0}</span>
        <span><b>Alerty ekonomiczne:</b> {analysis?.economicAlertDays ?? 0}</span>
        {analysis && !analysis.costComplete && <span className="workforce-planning-alert"><b>Plan:</b> niepełne dane kosztowe</span>}
        {analysis && !analysis.actualCostComplete && <span className="workforce-planning-alert"><b>Wykonanie:</b> niepełne dane kosztowe</span>}
      </div>

      {forecasts.length > 0 && (
        <div className="workforce-forecast-list">
          {forecasts.map(item => (
            <button key={item.id} type="button" className="workforce-forecast-chip" onClick={() => openForecast(item)}>
              <strong>{periodLabels[item.periodType] ?? item.periodType}</strong>
              <span>{item.dateFrom}{item.dateTo !== item.dateFrom ? ` – ${item.dateTo}` : ""}</span>
              <span>{money(item.salesForecast)}</span>
              {item.guestsForecast != null && <span>{item.guestsForecast} gości</span>}
            </button>
          ))}
        </div>
      )}

      <div className="table-responsive workforce-planning-table-wrapper">
        <table className="table table-sm align-middle workforce-planning-table">
          <thead>
            <tr>
              <th>Dzień</th>
              <th>Sprzedaż</th>
              <th>Prognozy</th>
              <th>Goście / pogoda</th>
              <th>Obsada</th>
              <th>Godziny PLAN / WYK</th>
              <th>Koszt PLAN / WYK</th>
              <th>%COL PLAN / WYK</th>
              <th>SPMH PLAN / WYK</th>
              <th>Ocena</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {days.map(day => {
              const alerts = [];
              if (day.understaffed) alerts.push("brak obsady");
              if (day.overstaffCount > 0) alerts.push(`nadmiar ${day.overstaffCount} os.`);
              if (day.colExceeded) alerts.push("COL ponad target");
              if (day.spmhBelowTarget) alerts.push("SPMH poniżej targetu");
              if (day.laborHoursBudgetExceeded) alerts.push("budżet godzin przekroczony");
              if (!day.costComplete) alerts.push("koszt planu niepełny");
              if (!day.actualCostComplete) alerts.push("koszt wykonania niepełny");
              const plan = day.plan || {};
              return (
                <tr key={day.date} className={day.reductionSuggested ? "workforce-planning-row-over" : day.understaffed ? "workforce-planning-row-under" : ""}>
                  <td><strong>{day.date}</strong></td>
                  <td>
                    <div>Plan ręczny: {money(plan.plannedSales)}</div>
                    <div>Wykonanie: {money(plan.actualSales)}</div>
                    <small>Podstawa planu: {money(day.salesBasis)} · {salesBasisLabels[day.salesBasisSource] ?? day.salesBasisSource}</small>
                  </td>
                  <td>
                    <div>D: {money(day.dailyForecastSales)}</div>
                    <div>M: {money(day.monthlyForecastSales)}</div>
                    <div>R: {money(day.annualForecastSales)}</div>
                  </td>
                  <td>
                    <div>{plan.expectedGuests ?? day.dailyForecastGuests ?? "—"} gości</div>
                    <small className="d-block">{plan.weatherForecast || day.dailyForecastWeather || "—"}</small>
                    {(plan.weatherTemperatureMin != null || plan.weatherTemperatureMax != null) && <small className="d-block">temp. {metric(plan.weatherTemperatureMin)}–{metric(plan.weatherTemperatureMax)} °C</small>}
                    {plan.weatherPrecipitationProbabilityMax != null && <small className="d-block">opady: {plan.weatherPrecipitationProbabilityMax}% · {metric(plan.weatherPrecipitationSum)} mm</small>}
                    {plan.weatherWindSpeedMax != null && <small className="d-block">wiatr max {metric(plan.weatherWindSpeedMax)} km/h</small>}
                    {plan.weatherDemandAdjustmentPercent != null && Number(plan.weatherDemandAdjustmentPercent) !== 0 && <small className="d-block"><b>Korekta zapotrzebowania: {signed(plan.weatherDemandAdjustmentPercent, "%")}</b></small>}
                  </td>
                  <td>
                    <div><b>Plan:</b> {day.assignedStaff} os. · {day.assignedShiftCount} zmian</div>
                    <div><b>RCP:</b> {day.actualStaff ?? 0} os.</div>
                    <small>min {day.minimumStaff ?? "—"} / max {day.maximumStaff ?? "—"}{(plan.minimumStaff == null && day.minimumStaff != null) || (plan.maximumStaff == null && day.maximumStaff != null) ? " · dział" : ""}</small>
                    {day.openShiftCount > 0 && <div className="workforce-planning-shortage">{day.openShiftCount} otwartych</div>}
                  </td>
                  <td>
                    <div><b>{hours(day.plannedHours)}</b> / {hours(day.actualHours)}</div>
                    <small className={deltaClass(day.hoursDelta)}>Bilans {signed(day.hoursDelta, " h")}</small>
                    <small className={day.laborHoursBudget != null ? (day.laborHoursBudgetExceeded ? "workforce-planning-alert d-block" : "workforce-planning-metric-ok d-block") : "d-block"}>Budżet: {day.laborHoursBudget == null ? "—" : hours(day.laborHoursBudget)}{plan.laborHoursBudget == null && day.laborHoursBudget != null ? " · dział" : ""}</small>
                    {day.maxLaborHoursBySpmh != null && <small className="d-block">Limit wg SPMH: {hours(day.maxLaborHoursBySpmh)}</small>}
                    {Number(day.excessLaborHoursBudget || 0) > 0 && <small className="workforce-planning-alert d-block">ponad budżet {hours(day.excessLaborHoursBudget)}</small>}
                  </td>
                  <td>
                    <div><b>{money(day.plannedLaborCost)}</b> / {money(day.actualLaborCost)}</div>
                    <small className={deltaClass(day.laborCostDelta)}>Bilans {signed(day.laborCostDelta, " zł")}</small>
                    {day.maxLaborCostByCol != null && <small className="d-block">Limit wg %COL: {money(day.maxLaborCostByCol)}</small>}
                    {Number(day.excessLaborCost || 0) > 0 && <small className="workforce-planning-alert d-block">ponad limit {money(day.excessLaborCost)}</small>}
                  </td>
                  <td>
                    <div className={day.targetColPercent != null && day.colPlanPercent != null ? (day.colExceeded ? "workforce-planning-alert" : "workforce-planning-metric-ok") : ""}>{percent(day.colPlanPercent)} / {percent(day.colActualPercent)}</div>
                    <small>target {percent(day.targetColPercent)}{plan.targetColPercent == null && day.targetColPercent != null ? " · dział" : ""}</small>
                  </td>
                  <td>
                    <div className={day.targetSpmh != null && day.spmhPlan != null ? (day.spmhBelowTarget ? "workforce-planning-alert" : "workforce-planning-metric-ok") : ""}>{day.spmhPlan == null ? "—" : `${metric(day.spmhPlan)} zł/h`} / {day.spmhActual == null ? "—" : `${metric(day.spmhActual)} zł/h`}</div>
                    <small>target {day.targetSpmh == null ? "—" : `${metric(day.targetSpmh)} zł/h`}{plan.targetSpmh == null && day.targetSpmh != null ? " · dział" : ""}</small>
                  </td>
                  <td>
                    {alerts.length === 0 ? <span className="workforce-planning-ok">OK</span> : alerts.map(alert => <span key={alert} className="workforce-planning-alert-badge">{alert}</span>)}
                    {day.reductionSuggested && (
                      <button type="button" className="workforce-planning-send-home" onClick={() => openSendHome(day)}>Puszczamy do domu</button>
                    )}
                  </td>
                  <td><Button size="sm" variant="outline-primary" onClick={() => openDay(day)}>Edytuj</Button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal show={!!sendHomeDay} onHide={() => setSendHomeDay(null)} centered size="xl">
        <Modal.Header closeButton><Modal.Title>Puszczamy do domu · {sendHomeDay?.date}</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="workforce-send-home-summary">
            <span><b>Nadwyżka ponad budżet godzin:</b> {hours(sendHomeDay?.excessLaborHoursBudget)}</span>
            <span><b>Nadwyżka godzin wg SPMH:</b> {hours(sendHomeDay?.excessLaborHours)}</span>
            <span><b>Nadwyżka kosztu wg %COL:</b> {money(sendHomeDay?.excessLaborCost)}</span>
            <span><b>Nadwyżka obsady:</b> {sendHomeDay?.overstaffCount ?? 0} os.</span>
          </div>

          <div className="workforce-send-home-controls">
            <Form.Group>
              <Form.Label>Godzina wcześniejszego wyjścia</Form.Label>
              <Form.Control type="time" value={sendHomeTime} onChange={event => setSendHomeTime(event.target.value)} />
            </Form.Group>
            <Button variant="outline-secondary" onClick={() => setSendHomeTime(timeString(new Date()))} disabled={sendHomeDay?.date !== dateString(new Date())}>Teraz</Button>
            <div className="schedule-section-subtitle">Ranking pokazuje oszczędność i efekt dla COL/SPMH po skróceniu konkretnej zmiany. System nie rekomenduje zejścia poniżej minimalnej obsady ani pozostawienia stanowiska bez aktywnej osoby.</div>
          </div>

          {sendHomeLoading ? (
            <div className="schedule-empty">Przeliczanie kandydatów…</div>
          ) : sendHomeCandidates.length === 0 ? (
            <div className="schedule-empty">Brak aktywnych zmian możliwych do skrócenia o tej godzinie.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle workforce-send-home-table">
                <thead>
                  <tr>
                    <th>Pracownik</th>
                    <th>Stanowisko</th>
                    <th>Zmiana</th>
                    <th>Oszczędność</th>
                    <th>Po zmianie</th>
                    <th>Obsada</th>
                    <th>Bezpieczeństwo</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sendHomeCandidates.map(candidate => {
                    const unsafe = candidate.dayUnderstaffedBeforeRelease || !candidate.minimumStaffRespected || !candidate.positionCoveragePreserved || !candidate.locationCoveragePreserved;
                    return (
                      <tr key={candidate.assignmentId} className={candidate.recommended ? "workforce-send-home-recommended" : unsafe ? "workforce-send-home-unsafe" : ""}>
                        <td><strong>{candidate.employeeFullName}</strong>{candidate.recommended && <span className="workforce-planning-ok d-block">rekomendowany</span>}</td>
                        <td>
                          <div>{candidate.positionName || "—"}</div>
                          {candidate.locationName && <small className="d-block text-muted">{candidate.locationName}</small>}
                        </td>
                        <td>{String(candidate.startsAt).slice(11, 16)}–{String(candidate.currentEndsAt).slice(11, 16)} → <b>{sendHomeTime}</b></td>
                        <td><div>{hours(candidate.savedHours)}</div><small>{money(candidate.savedLaborCost)}</small></td>
                        <td><div>COL {percent(candidate.projectedColPercent)}</div><div>SPMH {candidate.projectedSpmh == null ? "—" : `${metric(candidate.projectedSpmh)} zł/h`}</div><small>{candidate.remainingEconomicAlerts ?? 0} alertów po zmianie</small></td>
                        <td>
                          <div>{candidate.staffAfterRelease} os.</div>
                          <small className="d-block">na stanowisku: {candidate.samePositionStaffAfterRelease}</small>
                          {candidate.locationName && (
                            <>
                              <small className="d-block">w lokalizacji: {candidate.sameLocationStaffAfterRelease}</small>
                              {candidate.positionName && <small className="d-block">stanowisko + lokalizacja: {candidate.samePositionAndLocationStaffAfterRelease}</small>}
                            </>
                          )}
                        </td>
                        <td>
                          {candidate.dayUnderstaffedBeforeRelease && <span className="workforce-planning-alert-badge">dzień ma już braki</span>}
                          {candidate.minimumStaffRespected ? <span className="workforce-planning-ok d-block">minimum OK</span> : <span className="workforce-planning-alert-badge">poniżej minimum</span>}
                          {candidate.positionCoveragePreserved ? <span className="workforce-planning-ok d-block">stanowisko OK</span> : <span className="workforce-planning-alert-badge">brak obsady stanowiska</span>}
                          {candidate.locationName && (candidate.locationCoveragePreserved
                            ? <span className="workforce-planning-ok d-block">lokalizacja OK</span>
                            : <span className="workforce-planning-alert-badge">brak obsady lokalizacji</span>)}
                          {(candidate.warnings || []).map((warning, index) => <small key={index} className="d-block workforce-send-home-warning">{warning}</small>)}
                        </td>
                        <td className="text-end"><Button size="sm" variant={unsafe ? "outline-warning" : "success"} onClick={() => applySendHome(candidate)} disabled={saving || (unsafe && (!sendHomeOverride || !sendHomeReason.trim()))}>Zakończ o {sendHomeTime}</Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {sendHomeCandidates.some(candidate => candidate.dayUnderstaffedBeforeRelease || !candidate.minimumStaffRespected || !candidate.positionCoveragePreserved || !candidate.locationCoveragePreserved) && (
            <div className="workforce-send-home-override">
              <Form.Check
                type="checkbox"
                id="send-home-override"
                checked={sendHomeOverride}
                onChange={event => setSendHomeOverride(event.target.checked)}
                label="Zezwól na świadome nadpisanie zabezpieczenia obsady"
              />
              {sendHomeOverride && <Form.Control className="mt-2" value={sendHomeReason} onChange={event => setSendHomeReason(event.target.value)} placeholder="Powód decyzji jest wymagany przy nadpisaniu" />}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setSendHomeDay(null)}>Zamknij</Button></Modal.Footer>
      </Modal>

      <Modal show={!!dayForm} onHide={() => setDayForm(null)} centered backdrop="static">
        <Form onSubmit={saveDay}>
          <Modal.Header closeButton><Modal.Title>Plan dnia · {dayForm?.planningDate}</Modal.Title></Modal.Header>
          <Modal.Body className="workforce-planning-form-grid">
            <Form.Group><Form.Label>Sprzedaż planowana</Form.Label><Form.Control type="number" min="0" step="0.01" value={dayForm?.plannedSales ?? ""} onChange={e => setDayForm(prev => ({ ...prev, plannedSales: e.target.value }))} /></Form.Group>
            <Form.Group><Form.Label>Sprzedaż wykonana</Form.Label><Form.Control type="number" min="0" step="0.01" value={dayForm?.actualSales ?? ""} onChange={e => setDayForm(prev => ({ ...prev, actualSales: e.target.value }))} /></Form.Group>
            <Form.Group><Form.Label>Prognozowana liczba gości</Form.Label><Form.Control type="number" min="0" step="1" value={dayForm?.expectedGuests ?? ""} onChange={e => setDayForm(prev => ({ ...prev, expectedGuests: e.target.value }))} /></Form.Group>
            <Form.Group><Form.Label>Pogoda / opis warunków</Form.Label><Form.Control value={dayForm?.weatherForecast ?? ""} onChange={e => setDayForm(prev => ({ ...prev, weatherForecast: e.target.value }))} /></Form.Group>
            {(dayForm?.weatherProvider || dayForm?.weatherFetchedAt) && (
              <div className="workforce-planning-form-wide small text-muted">
                <b>Dane pobrane:</b> {dayForm.weatherProvider || "dostawca pogody"}
                {(dayForm.weatherTemperatureMin != null || dayForm.weatherTemperatureMax != null) && <> · {metric(dayForm.weatherTemperatureMin)}–{metric(dayForm.weatherTemperatureMax)} °C</>}
                {dayForm.weatherPrecipitationProbabilityMax != null && <> · opady {dayForm.weatherPrecipitationProbabilityMax}% / {metric(dayForm.weatherPrecipitationSum)} mm</>}
                {dayForm.weatherWindSpeedMax != null && <> · wiatr max {metric(dayForm.weatherWindSpeedMax)} km/h</>}
              </div>
            )}
            <Form.Group>
              <Form.Label>Korekta zapotrzebowania wg pogody (%)</Form.Label>
              <Form.Control type="number" min="-100" max="300" step="1" value={dayForm?.weatherDemandAdjustmentPercent ?? ""} onChange={e => setDayForm(prev => ({ ...prev, weatherDemandAdjustmentPercent: e.target.value }))} />
              <Form.Text>Przykład: -20 zmniejsza liczbę wymaganych osób o 20%, +15 zwiększa o 15%. Generator zaokrągla wynik do pełnej osoby.</Form.Text>
            </Form.Group>
            <Form.Group><Form.Label>%COL TARGET</Form.Label><Form.Control type="number" min="0" max="100" step="0.01" placeholder={departmentPlanningDefaults?.defaultTargetColPercent ?? ""} value={dayForm?.targetColPercent ?? ""} onChange={e => setDayForm(prev => ({ ...prev, targetColPercent: e.target.value }))} /></Form.Group>
            <Form.Group><Form.Label>SPMH TARGET</Form.Label><Form.Control type="number" min="0.01" step="0.01" placeholder={departmentPlanningDefaults?.defaultTargetSpmh ?? ""} value={dayForm?.targetSpmh ?? ""} onChange={e => setDayForm(prev => ({ ...prev, targetSpmh: e.target.value }))} /></Form.Group>
            <Form.Group><Form.Label>Budżet roboczogodzin</Form.Label><Form.Control type="number" min="0" step="0.25" placeholder={departmentPlanningDefaults?.defaultLaborHoursBudget ?? ""} value={dayForm?.laborHoursBudget ?? ""} onChange={e => setDayForm(prev => ({ ...prev, laborHoursBudget: e.target.value }))} /></Form.Group>
            <Form.Group><Form.Label>Minimalna obsada</Form.Label><Form.Control type="number" min="0" step="1" placeholder={departmentPlanningDefaults?.defaultMinimumStaff ?? ""} value={dayForm?.minimumStaff ?? ""} onChange={e => setDayForm(prev => ({ ...prev, minimumStaff: e.target.value }))} /></Form.Group>
            <Form.Group><Form.Label>Maksymalna obsada</Form.Label><Form.Control type="number" min="0" step="1" placeholder={departmentPlanningDefaults?.defaultMaximumStaff ?? ""} value={dayForm?.maximumStaff ?? ""} onChange={e => setDayForm(prev => ({ ...prev, maximumStaff: e.target.value }))} /></Form.Group>
            <Form.Group className="workforce-planning-form-wide"><Form.Label>Uwagi</Form.Label><Form.Control as="textarea" rows={2} value={dayForm?.notes ?? ""} onChange={e => setDayForm(prev => ({ ...prev, notes: e.target.value }))} /></Form.Group>
          </Modal.Body>
          <Modal.Footer>
            {dayForm?.id && <Button variant="outline-danger" onClick={deleteDay} disabled={saving}>Usuń dane dnia</Button>}
            <Button variant="secondary" onClick={() => setDayForm(null)} disabled={saving}>Anuluj</Button>
            <Button type="submit" variant="primary" disabled={saving}>Zapisz</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={!!forecastForm} onHide={() => setForecastForm(null)} centered backdrop="static">
        <Form onSubmit={saveForecast}>
          <Modal.Header closeButton><Modal.Title>{forecastForm?.id ? "Edycja prognozy" : "Nowa prognoza"}</Modal.Title></Modal.Header>
          <Modal.Body className="workforce-planning-form-grid">
            <Form.Group><Form.Label>Typ prognozy</Form.Label><Form.Select value={forecastForm?.periodType ?? "DAILY"} onChange={e => setForecastForm(prev => ({ ...prev, periodType: e.target.value }))}><option value="DAILY">Dzienna</option><option value="MONTHLY">Miesięczna</option><option value="ANNUAL">Roczna</option></Form.Select></Form.Group>
            <Form.Group><Form.Label>{forecastForm?.periodType === "ANNUAL" ? "Rok" : forecastForm?.periodType === "MONTHLY" ? "Miesiąc" : "Dzień"}</Form.Label><Form.Control type="date" value={forecastForm?.referenceDate ?? ""} onChange={e => setForecastForm(prev => ({ ...prev, referenceDate: e.target.value }))} required /></Form.Group>
            <Form.Group><Form.Label>Prognoza sprzedaży</Form.Label><Form.Control type="number" min="0" step="0.01" value={forecastForm?.salesForecast ?? ""} onChange={e => setForecastForm(prev => ({ ...prev, salesForecast: e.target.value }))} /></Form.Group>
            <Form.Group><Form.Label>Prognoza liczby gości</Form.Label><Form.Control type="number" min="0" step="1" value={forecastForm?.guestsForecast ?? ""} onChange={e => setForecastForm(prev => ({ ...prev, guestsForecast: e.target.value }))} /></Form.Group>
            <Form.Group className="workforce-planning-form-wide"><Form.Label>Prognoza pogody / opis warunków</Form.Label><Form.Control value={forecastForm?.weatherSummary ?? ""} onChange={e => setForecastForm(prev => ({ ...prev, weatherSummary: e.target.value }))} /></Form.Group>
            <Form.Group className="workforce-planning-form-wide"><Form.Label>Uwagi</Form.Label><Form.Control as="textarea" rows={2} value={forecastForm?.notes ?? ""} onChange={e => setForecastForm(prev => ({ ...prev, notes: e.target.value }))} /></Form.Group>
          </Modal.Body>
          <Modal.Footer>
            {forecastForm?.id && <Button variant="outline-danger" onClick={deleteForecast} disabled={saving}>Usuń prognozę</Button>}
            <Button variant="secondary" onClick={() => setForecastForm(null)} disabled={saving}>Anuluj</Button>
            <Button type="submit" variant="primary" disabled={saving}>Zapisz</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkforcePlanningPanel;


